import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, ArrowLeft, Check, ChevronLeft, ChevronRight, Copy, Loader2, Plus, Trash2, X } from "lucide-react";
import Layout from "@/components/Layout";
import { LoadingPanel } from "@/components/Bits";
import { Pill, SectionTag, SplitBar, WRITER_COLOURS } from "@/components/Music";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { DatePicker } from "@/components/Calendar";
import { useConfirm } from "@/components/Confirm";
import { useSettings } from "@/context/SettingsContext";
import { api, apiError, formatDate, formatDateTime } from "@/lib/api";
import { cacheGet, cacheKey, cacheSet } from "@/lib/cache";
import { useTenant } from "@/context/TenantContext";
import { cn } from "@/lib/utils";

const BLANK = {
  title: "",
  artist: "",
  featured: "",
  riddim: "",
  length: "",
  voice: "",
  harmony: "",
  mix: "",
  mastered: "",
  original_release_date: "",
  release_date: "",
  released: "",
  isrc: "",
  cat: "",
  upc: "",
  label: "",
  distributor: "",
  pub_date: "",
  pro: "",
  ipi: "",
  notes: "",
  reg_status: "",
  reg_status_note: "",
  writers: [{ writer_id: null, name: "", share: 100, pro: "", ipi: "" }],
  regs: {},
};

const inputCls =
  "w-full h-10 rounded-lg border border-line bg-card px-3 text-[13.5px] text-ink outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 placeholder:text-muted2/70";

function Field({ label, hint, hintTone, children, className }) {
  return (
    <label className={cn("block min-w-0", className)}>
      <span className="block text-[12px] font-semibold text-ink mb-1.5">{label}</span>
      {children}
      {hint && <span className={cn("block text-[11.5px] mt-1.5", hintTone === "bad" ? "text-stock-out" : hintTone === "good" ? "text-stock-in" : "text-muted2")}>{hint}</span>}
    </label>
  );
}

function Segmented({ label, options, value, onChange }) {
  return (
    <div className="min-w-0">
      <div className="text-[12px] font-semibold text-ink mb-1.5">{label}</div>
      <div role="radiogroup" aria-label={label} className="flex gap-0.5 p-[3px] rounded-lg bg-surface border border-line">
        {options.map((o) => (
          <button
            key={o}
            type="button"
            role="radio"
            aria-checked={value === o}
            onClick={() => onChange(value === o ? "" : o)}
            className={cn(
              "flex-1 h-8 rounded-md text-[12px] font-semibold px-1.5 whitespace-nowrap transition-colors",
              value === o ? "bg-card text-ink shadow-sm" : "text-muted2 hover:text-ink"
            )}
          >
            {o}
          </button>
        ))}
      </div>
    </div>
  );
}

function Card({ k, hint, children, extra }) {
  return (
    <section className="panel overflow-hidden">
      <div className="flex items-center gap-3 px-5 py-3.5 border-b border-line">
        <SectionTag k={k} />
        <span className="text-[12.5px] text-muted2 hidden sm:inline">{hint}</span>
        <div className="flex-1" />
        {extra}
      </div>
      <div className="p-5 space-y-4">{children}</div>
    </section>
  );
}

export function ListInput({ value, onChange, options, placeholder, id }) {
  return (
    <>
      <input className={inputCls} list={id} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
      <datalist id={id}>
        {options.map((o) => (
          <option key={o} value={o} />
        ))}
      </datalist>
    </>
  );
}

const ISRC_RE = /^[A-Z]{2}[A-Z0-9]{3}\d{7}$/;

function toForm(data) {
  if (!data) return null;
  return {
    ...BLANK,
    ...Object.fromEntries(Object.keys(BLANK).map((k) => [k, data[k] ?? BLANK[k]])),
    writers: Array.isArray(data.writers) ? data.writers : [],
    regs: data.regs || {},
  };
}

export default function TrackEditor() {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { settings } = useSettings();
  const { tenantId } = useTenant();
  // Opening a track you've already looked at paints it at once, then refreshes.
  const CK = cacheKey(`track:${id}`, tenantId);
  const [form, setForm] = useState(() => (isNew ? BLANK : toForm(cacheGet(CK))));
  const [track, setTrack] = useState(() => (isNew ? null : cacheGet(CK) || null));
  const [writers, setWriters] = useState([]);
  const [values, setValues] = useState({ artist: [], label: [], distributor: [], riddim: [] });
  const [busy, setBusy] = useState(false);
  // Opened from an album page: step through its tracks without going back each time.
  const [params] = useSearchParams();
  const albumId = params.get("album");
  const AK = cacheKey(`album:${albumId}`, tenantId);
  const [album, setAlbum] = useState(() => (albumId ? cacheGet(AK) : null));
  const [pending, setPending] = useState(null);
  const [asSingle, setAsSingle] = useState(false);
  useEffect(() => window.scrollTo(0, 0), []);

  useEffect(() => {
    if (!albumId) return setAlbum(null);
    setAlbum(cacheGet(AK));
    api
      .get(`/albums/${albumId}`)
      .then(({ data }) => {
        cacheSet(AK, data);
        setAlbum(data);
      })
      .catch(() => setAlbum(null));
  }, [albumId, AK]);

  useEffect(() => {
    api.get("/writers").then(({ data }) => setWriters(data)).catch(() => {});
    api.get("/values").then(({ data }) => setValues(data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (isNew) {
      setForm(BLANK);
      setTrack(null);
      return;
    }
    setPending(null);
    setAsSingle(false);
    const seen = cacheGet(CK);
    if (seen) {
      setTrack(seen);
      setForm(toForm(seen));
    }
    api
      .get(`/tracks/${id}`)
      .then(({ data }) => {
        if (data.moved_to) {
          // this copy was merged into another track
          cacheSet(CK, null);
          navigate(`/catalog/${data.moved_to}${window.location.search}`, { replace: true });
          return;
        }
        cacheSet(CK, data);
        setTrack(data);
        setForm(toForm(data));
      })
      .catch((err) => {
        toast.error(apiError(err));
        navigate("/catalog");
      });
  }, [id, isNew, navigate, CK]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: typeof v === "object" && v?.target ? v.target.value : v }));
  const setWriter = (i, patch) => setForm((f) => ({ ...f, writers: f.writers.map((w, j) => (j === i ? { ...w, ...patch } : w)) }));
  const setReg = (key, patch) => setForm((f) => ({ ...f, regs: { ...f.regs, [key]: { done: false, date: "", ...(f.regs[key] || {}), ...patch } } }));

  const total = useMemo(() => (form?.writers || []).reduce((a, w) => a + (Number(w.share) || 0), 0), [form]);
  const isrc = (form?.isrc || "").replace(/[^A-Za-z0-9]/g, "").toUpperCase();
  const upc = (form?.upc || "").replace(/\D/g, "");

  const checks = form
    ? [
        ["ISRC looks right", ISRC_RE.test(isrc)],
        ["ISRC not used on another track", !(track?.issues || []).some((i) => i.code === "dup_isrc")],
        ["UPC is 12 or 13 digits", upc.length === 12 || upc.length === 13],
        ["Release date set", !!form.release_date],
        ["Label and distributor set", !!(form.label && form.distributor)],
        ["Writers add up to 100%", form.writers.length > 0 && Math.abs(total - 100) < 0.01],
        ["Every writer has a PRO", form.writers.length > 0 && form.writers.every((w) => w.pro)],
        ["Every writer has an IPI", form.writers.length > 0 && form.writers.every((w) => w.ipi)],
        ["Registered at BMI", !!form.regs?.BMI?.done || form.regs?.BMI?.status === "na"],
        ["Registered at the MLC", !!form.regs?.MLC?.done || form.regs?.MLC?.status === "na"],
      ]
    : [];
  const passed = checks.filter((c) => c[1]).length;

  // The single (unless its own date or UPC is one of its albums') then every album it's on — see /albums.
  const releaseRows = useMemo(() => {
    const albums = track?.albums || [];
    if (!albums.length || !form) return [];
    const digits = (x) => String(x || "").replace(/\D/g, "");
    const own = { date: form.release_date || "", upc: digits(form.upc) };
    // the UPC decides which release it is; when either has no UPC, the same date counts
    const albumOnly = albums.some((a) => (own.upc && digits(a.upc) ? digits(a.upc) === own.upc : own.date && a.date === own.date));
    const rows = [
      ...(!albumOnly && own.date ? [{ key: "single", kind: "Single", name: form.title || "Single", date: own.date, upc: form.upc, distributor: form.distributor }] : []),
      ...albums.map((a) => ({ key: a.album_id, kind: "Album", id: a.album_id, name: a.name || "Untitled album", date: a.date, upc: a.upc, distributor: a.distributor })),
    ];
    const dated = rows.filter((r) => r.date).sort((x, y) => (x.date < y.date ? -1 : 1));
    if (rows.length > 1 && dated.length) {
      dated[0].tag = "Original";
      if (dated.length > 1) dated[dated.length - 1].tag = "Latest";
    }
    return rows;
  }, [track, form]);

  const dirty = !isNew && !!track && !!form && JSON.stringify(form) !== JSON.stringify(toForm(track));
  const list = useMemo(() => album?.track_list || [], [album]);
  const pos = list.findIndex((t) => t.track_id === id);
  const inAlbum = !!albumId && pos >= 0;
  const hrefFor = (tid) => `/catalog/${tid}?album=${albumId}`;

  // the next and previous tracks open instantly
  useEffect(() => {
    if (!inAlbum) return;
    [list[pos - 1], list[pos + 1]].filter(Boolean).forEach((t) => {
      const k = cacheKey(`track:${t.track_id}`, tenantId);
      if (cacheGet(k) == null) api.get(`/tracks/${t.track_id}`).then(({ data }) => cacheSet(k, data)).catch(() => {});
    });
  }, [inAlbum, pos, list, tenantId]);

  const go = useCallback(
    (j) => {
      if (!inAlbum || j < 0 || j >= list.length || j === pos) return;
      if (dirty) return setPending(j);
      navigate(hrefFor(list[j].track_id), { replace: true });
    },
    [inAlbum, list, pos, dirty, navigate] // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    if (!inAlbum) return undefined;
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey || e.target.closest?.("input, textarea, select, [contenteditable=true]")) return;
      if (e.key === "[") go(pos - 1);
      else if (e.key === "]") go(pos + 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [inAlbum, go, pos]);

  // An album-only song takes its release date and UPC from its albums — the first one out is the "original".
  const albumsByDate = [...(track?.albums || [])].filter((a) => a.date).sort((a, b) => a.date.localeCompare(b.date));
  const firstAlbum = albumsByDate[0] || track?.albums?.[0];
  const fromAlbums = !isNew && !!track?.albums?.length && !releaseRows.some((r) => r.kind === "Single") && !asSingle;

  const save = async (nextIdx) => {
    if (!form.title.trim()) return toast.error("Give the track a title");
    if (asSingle && !form.release_date) return toast.error("Enter the single's release date — or click “It was never a single”");
    setBusy(true);
    const own = fromAlbums && firstAlbum ? { release_date: firstAlbum.date || form.release_date, upc: firstAlbum.upc || form.upc } : {};
    // a single that came out before its albums is the song's original release
    const firstOut = [form.release_date, ...albumsByDate.map((x) => x.date)].filter(Boolean).sort()[0];
    const body = {
      ...form,
      ...own,
      ...(asSingle && firstOut && (!form.original_release_date || form.original_release_date > firstOut) ? { original_release_date: firstOut } : {}),
      writers: form.writers.filter((w) => w.name.trim()).map((w) => ({ ...w, share: w.share === "" || w.share == null ? null : Number(w.share) })),
    };
    try {
      const { data } = isNew ? await api.post("/tracks", body) : await api.put(`/tracks/${id}`, body);
      if (!isNew) cacheSet(CK, data);
      toast.success(isNew ? `“${data.title}” added` : `“${data.title}” saved`);
      if (typeof nextIdx === "number" && inAlbum && list[nextIdx]) {
        setTrack(data);
        setForm(toForm(data));
        setPending(null);
        navigate(hrefFor(list[nextIdx].track_id), { replace: true });
        return;
      }
      // back to the list you came from (keeps its filters and scroll); straight to the catalog if opened from a link
      if ((window.history.state?.idx ?? 0) > 0) navigate(-1);
      else navigate("/catalog", { replace: true });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!(await confirm({ title: `Delete “${form.title}”?`, body: "This can't be undone.", confirmText: "Delete", destructive: true }))) return;
    try {
      await api.delete(`/tracks/${id}`);
      toast.success("Deleted");
      navigate("/catalog");
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const duplicate = async () => {
    try {
      const { data } = await api.post(`/tracks/${id}/duplicate`);
      toast.success("Copied — codes cleared on the copy");
      navigate(`/catalog/${data.track_id}`);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const pickWriter = (i, name) => {
    const w = writers.find((x) => x.name.toLowerCase() === name.trim().toLowerCase());
    if (w) setWriter(i, { name: w.name, writer_id: w.writer_id, pro: w.pro || "", ipi: w.ipi || "" });
    else setWriter(i, { name, writer_id: null });
  };

  if (!form)
    return (
      <Layout title="Loading…">
        <LoadingPanel rows={10} />
      </Layout>
    );

  const po = settings.production_options;
  const sub = (
    <span>
      <Link to="/catalog" className="link-quiet no-underline font-medium">
        Catalog
      </Link>{" "}
      / {isNew ? "New track" : form.artist || "Unknown artist"}
    </span>
  );

  return (
    <Layout
      title={isNew ? "New track" : form.title || "Untitled"}
      subtitle={sub}
      action={
        <>
          <Button variant="outline" onClick={() => navigate(-1)} className="hidden sm:inline-flex">
            Cancel
          </Button>
          <Button onClick={() => save()} disabled={busy} className="bg-brand hover:bg-brand-hover">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
            {isNew ? "Add track" : "Save"}
          </Button>
        </>
      }
    >
      {inAlbum && (
        <AlbumBar
          album={album}
          pos={pos}
          dirty={dirty}
          busy={busy}
          pending={pending}
          onGo={go}
          onBack={() => ((window.history.state?.idx ?? 0) > 0 ? navigate(-1) : navigate(`/albums/${albumId}`))}
          onSaveGo={(j) => save(j)}
          onDiscardGo={(j) => {
            setForm(toForm(track));
            setPending(null);
            navigate(hrefFor(list[j].track_id), { replace: true });
          }}
          onStay={() => setPending(null)}
        />
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
        className="flex flex-col xl:flex-row gap-5 items-start"
      >
        <div className="flex-1 min-w-0 w-full space-y-4">
          <Card k="track" hint="Who and what">
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Track title">
                <input className={inputCls} value={form.title} onChange={set("title")} required autoFocus={isNew} />
              </Field>
              <Field label="Riddim / album">
                <ListInput id="riddims" value={form.riddim} onChange={set("riddim")} options={values.riddim?.map((v) => v.value) || []} />
              </Field>
            </div>
            <div className="grid sm:grid-cols-[1fr_1fr_140px] gap-4">
              <Field label="Artist">
                <ListInput id="artists" value={form.artist} onChange={set("artist")} options={values.artist.map((v) => v.value)} />
              </Field>
              <Field label="Featured artists">
                <input className={inputCls} value={form.featured} onChange={set("featured")} placeholder="Separate with commas" />
              </Field>
              <Field label="Length" hint="m:ss">
                <input className={cn(inputCls, "font-mono")} value={form.length} onChange={set("length")} placeholder="3:27" />
              </Field>
            </div>
          </Card>

          <Card k="recording" hint="From your production tracker">
            <div className="grid md:grid-cols-2 gap-4">
              <Segmented label="Voice" options={po.voice} value={form.voice} onChange={set("voice")} />
              <Segmented label="Harmony" options={po.harmony} value={form.harmony} onChange={set("harmony")} />
              <Segmented label="Mix" options={po.mix} value={form.mix} onChange={set("mix")} />
              <Segmented label="Mastered" options={po.mastered} value={form.mastered} onChange={set("mastered")} />
            </div>
          </Card>

          <Card k="release" hint={track?.albums?.length ? (releaseRows.some((r) => r.kind === "Single") ? "The single, then every album it's on" : `Released on ${track.albums.length === 1 ? "an album" : "albums"} — never as a single`) : "Dates and status"}>
            {releaseRows.length > 0 && (
              <div className="-mx-5 -mt-1 mb-5 border-b border-line">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[680px]">
                    <thead>
                      <tr className="border-b border-line">
                        {["", "Release", "Date", "UPC", "Distributor", ""].map((h, i) => (
                          <th key={i} className="t-head text-left px-5 py-2 font-medium">
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {releaseRows.map((r) => (
                        <tr
                          key={r.key}
                          className={cn(
                            "border-b border-line last:border-0",
                            r.kind === "Album" && "bg-sec-release-light/40 cursor-pointer hover:bg-sec-release-light/70",
                            r.id && r.id === albumId && "bg-brand-light/70 hover:bg-brand-light shadow-[inset_3px_0_0_hsl(var(--brand))]"
                          )}
                          onClick={r.kind === "Album" ? () => navigate(`/albums/${r.id}`) : undefined}
                        >
                          <td className="px-5 py-2.5 w-[90px]">
                            <Pill tone={r.kind === "Album" ? "brand" : "muted"}>{r.kind}</Pill>
                          </td>
                          <td className="px-5 py-2.5 text-[13px] font-semibold">
                            {r.name}
                            {r.id && r.id === albumId && <span className="mt-1 w-fit flex whitespace-nowrap px-2 py-0.5 rounded-full text-[11px] font-semibold bg-brand text-white">You came from here</span>}
                          </td>
                          <td className="px-5 py-2.5 text-[13px] whitespace-nowrap">{r.date ? formatDate(r.date) : "—"}</td>
                          <td className="px-5 py-2.5 font-mono text-[12.5px]">{r.upc || <span className="text-muted2">—</span>}</td>
                          <td className="px-5 py-2.5 text-[12.5px]">{r.distributor || <span className="text-muted2">—</span>}</td>
                          <td className="px-5 py-2.5 text-right whitespace-nowrap">
                            {r.tag && <Pill tone={r.tag === "Original" ? "brand" : "muted"}>{r.tag}</Pill>}{" "}
                            {r.kind === "Album" ? <span className="text-[12.5px] font-semibold text-brand">Open album →</span> : <span className="text-[12px] text-muted2">its own date &amp; UPC below</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {releaseRows.length > 1 && releaseRows.find((r) => r.tag === "Original") && (
                  <p className="px-5 py-2.5 text-[12px] text-muted2 border-t border-line">
                    First released <b className="text-ink">{formatDate(releaseRows.find((r) => r.tag === "Original").date)}</b> — the earliest of these.
                  </p>
                )}
              </div>
            )}
            {fromAlbums ? (
              <div className="grid md:grid-cols-3 gap-4 items-start">
                <div className="md:col-span-2 rounded-xl border border-line bg-surface/70 px-4 py-3">
                  <div className="eyebrow mb-1">Release date &amp; UPC</div>
                  <div className="text-[13.5px]">
                    Comes from its albums: first out on <b>{firstAlbum?.name}</b>, {firstAlbum?.date ? formatDate(firstAlbum.date) : "no date yet"} · UPC <span className="font-mono">{firstAlbum?.upc || "—"}</span>.
                  </div>
                  <div className="text-[12px] text-muted2 mt-1">
                    {albumsByDate.length > 1 && <>Also on {albumsByDate.slice(1).map((a) => `${a.name}, ${formatDate(a.date)}`).join("; ")}. </>}
                    The Catalog and exports show the first album's date and UPC. Change them on the album and this follows.
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setAsSingle(true);
                      setForm((f) => ({ ...f, release_date: "", upc: "" }));
                    }}
                    className="mt-2 text-[12.5px] font-semibold text-brand underline underline-offset-2"
                  >
                    It also came out as a single
                  </button>
                </div>
                <Segmented label="Released" options={settings.released_options} value={form.released} onChange={set("released")} />
              </div>
            ) : (
            <div className="grid md:grid-cols-3 gap-4">
              <Field label="Original release date" hint={track?.albums?.length ? "When the song first came out" : undefined}>
                <DatePicker value={form.original_release_date} onChange={set("original_release_date")} ariaLabel="Original release date" />
              </Field>
              <Field
                label={asSingle || releaseRows.some((r) => r.kind === "Single") ? "Single's release date" : "Release date"}
                hint={!track?.albums?.length ? undefined : releaseRows.some((r) => r.kind === "Single") ? "The single's own release — its UPC is under Codes" : asSingle ? "Enter the single's date, and its own UPC under Codes" : "Matches an album above, so it isn't counted as a single"}
                hintTone={asSingle && !form.release_date ? "bad" : null}
              >
                <DatePicker value={form.release_date} onChange={set("release_date")} ariaLabel="Release date" />
              </Field>
              <Segmented label="Released" options={settings.released_options} value={form.released} onChange={set("released")} />
            </div>
            )}
            {!fromAlbums && firstAlbum && (
              <button
                type="button"
                onClick={() => {
                  setForm((f) => ({ ...f, release_date: firstAlbum.date || f.release_date, upc: firstAlbum.upc || f.upc, original_release_date: albumsByDate[0]?.date || f.original_release_date }));
                  setAsSingle(false);
                }}
                className="text-[12.5px] font-semibold text-brand underline underline-offset-2 justify-self-start"
              >
                It was never a single — take its date and UPC from its albums
              </button>
            )}
          </Card>

          <Card k="codes" hint="Identifiers">
            <div className="grid md:grid-cols-3 gap-4">
              <Field
                label="ISRC"
                hint={!isrc ? "12 characters, e.g. QZPYN2363092" : ISRC_RE.test(isrc) ? (isrc !== form.isrc ? `Will be saved as ${isrc}` : "Looks right") : "Should be 2 letters, 3 letters/digits, then 7 digits"}
                hintTone={!isrc ? null : ISRC_RE.test(isrc) ? "good" : "bad"}
              >
                <input className={cn(inputCls, "font-mono uppercase")} value={form.isrc} onChange={set("isrc")} />
              </Field>
              {fromAlbums ? (
                <Field label="UPC" hint={`From ${firstAlbum?.name || "its album"} — change it on the album`}>
                  <div className={cn(inputCls, "font-mono flex items-center bg-surface/70 text-muted2")}>{firstAlbum?.upc || "—"}</div>
                </Field>
              ) : (
              <Field
                label={asSingle || releaseRows.some((r) => r.kind === "Single") ? "Single's UPC" : "UPC"}
                hint={
                  (asSingle || releaseRows.some((r) => r.kind === "Single")) && upc && (track?.albums || []).some((a) => (a.upc || "").replace(/\D/g, "") === upc)
                    ? `That's ${(track.albums || []).find((a) => (a.upc || "").replace(/\D/g, "") === upc).name}'s UPC — a single has its own`
                    : !upc ? (asSingle ? "The single's own UPC" : "12 or 13 digits") : upc.length === 12 || upc.length === 13 ? `${upc.length} digits` : `${upc.length} digits — should be 12 or 13`
                }
                hintTone={!upc ? null : (asSingle || releaseRows.some((r) => r.kind === "Single")) && (track?.albums || []).some((x) => (x.upc || "").replace(/\D/g, "") === upc) ? "bad" : upc.length === 12 || upc.length === 13 ? "good" : "bad"}
              >
                <input className={cn(inputCls, "font-mono")} value={form.upc} onChange={set("upc")} inputMode="numeric" />
              </Field>
              )}
              <Field label="CAT #">
                <input className={cn(inputCls, "font-mono")} value={form.cat} onChange={set("cat")} />
              </Field>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              <Field label="Label">
                <ListInput id="labels" value={form.label} onChange={set("label")} options={values.label.map((v) => v.value)} />
              </Field>
            </div>
          </Card>

          <Card k="publishing" hint="Writers and their shares">
            <div>
              <SplitBar writers={form.writers.map((w) => ({ share: Number(w.share) || 0 }))} tall />
              <div className="flex justify-between mt-2 text-[12px]">
                <span className="text-muted2">Pick a writer from your roster, or type a new name and they'll be added.</span>
                <span className={cn("font-mono font-semibold", Math.abs(total - 100) < 0.01 ? "text-stock-in" : "text-stock-out")}>
                  {Math.abs(total - 100) < 0.01 ? "100% allocated" : `${+total.toFixed(2)}% allocated`}
                </span>
              </div>
            </div>
            <datalist id="writer-names">
              {writers.map((w) => (
                <option key={w.writer_id} value={w.name}>
                  {w.stage_name}
                </option>
              ))}
            </datalist>
            <div className="hidden md:grid grid-cols-[14px_minmax(0,2fr)_minmax(0,1fr)_minmax(0,1.3fr)_96px_32px] gap-3 eyebrow text-[10px]">
              <span />
              <span>Writer</span>
              <span>PRO</span>
              <span>IPI</span>
              <span className="text-right">Share %</span>
              <span />
            </div>
            {form.writers.map((w, i) => (
              <div key={i} className="grid grid-cols-[14px_minmax(0,1fr)_32px] md:grid-cols-[14px_minmax(0,2fr)_minmax(0,1fr)_minmax(0,1.3fr)_96px_32px] gap-3 items-center">
                <span className={cn("w-3 h-3 rounded-[4px]", WRITER_COLOURS[i] || WRITER_COLOURS[3])} />
                <input className={inputCls} list="writer-names" aria-label={`Writer ${i + 1}`} value={w.name} placeholder="Writer name" onChange={(e) => pickWriter(i, e.target.value)} />
                <button type="button" aria-label="Remove writer" onClick={() => setForm((f) => ({ ...f, writers: f.writers.filter((_, j) => j !== i) }))} className="md:hidden p-1.5 text-muted2 hover:text-stock-out">
                  <X className="w-4 h-4" />
                </button>
                <span className="md:hidden" />
                <select aria-label="PRO" className={cn(inputCls, "md:col-auto col-span-2")} value={w.pro || ""} onChange={(e) => setWriter(i, { pro: e.target.value })}>
                  <option value="">PRO…</option>
                  {settings.lists.pros.map((p) => (
                    <option key={p}>{p}</option>
                  ))}
                </select>
                <span className="md:hidden" />
                <input aria-label="IPI" className={cn(inputCls, "font-mono col-span-2 md:col-auto", !w.ipi && "border-amber-300")} value={w.ipi || ""} placeholder="IPI #" onChange={(e) => setWriter(i, { ipi: e.target.value })} />
                <span className="md:hidden" />
                <input
                  aria-label="Share percent"
                  className={cn(inputCls, "font-mono text-right col-span-2 md:col-auto")}
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={w.share ?? ""}
                  onChange={(e) => setWriter(i, { share: e.target.value })}
                />
                <button type="button" aria-label="Remove writer" onClick={() => setForm((f) => ({ ...f, writers: f.writers.filter((_, j) => j !== i) }))} className="hidden md:block p-1.5 text-muted2 hover:text-stock-out">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
            <div className="flex gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => setForm((f) => ({ ...f, writers: [...f.writers, { writer_id: null, name: "", share: Math.max(0, 100 - total) || "", pro: "", ipi: "" }] }))}
                className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-dashed border-line text-[13px] font-semibold text-brand hover:border-brand-ring"
              >
                <Plus className="w-4 h-4" /> Add writer
              </button>
              {form.writers.length > 1 && (
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, writers: f.writers.map((w) => ({ ...w, share: +(100 / f.writers.length).toFixed(2) })) }))}
                  className="inline-flex items-center h-9 px-3 rounded-lg text-[13px] text-muted2 hover:text-ink"
                >
                  Split evenly
                </button>
              )}
            </div>
            <div className="grid md:grid-cols-3 gap-4 pt-2">
              <Field label="Published on">
                <DatePicker value={form.pub_date} onChange={set("pub_date")} ariaLabel="Published on" />
              </Field>
            </div>
          </Card>

          <Card k="mechanical" hint="Where this work is registered">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {settings.societies.map((s) => {
                const r = form.regs?.[s.key] || { status: "pending", date: "" };
                const st = r.status || (r.done ? "registered" : "pending");
                return (
                  <div key={s.key} className={cn("rounded-xl border p-3.5 space-y-2.5", st === "registered" ? "border-sec-mech/40 bg-sec-mech-light/50" : st === "rejected" ? "border-stock-out/30" : st === "na" ? "border-dashed border-line opacity-80" : "border-line")}>
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-mono text-[13px] font-semibold">{s.label}</span>
                      <select
                        value={st}
                        onChange={(e) => {
                          const v = e.target.value;
                          setReg(s.key, { status: v, done: v === "registered", date: v === "registered" ? r.date || new Date().toISOString().slice(0, 10) : "", note: v === "rejected" ? r.note || "" : "" });
                        }}
                        aria-label={`${s.label} status`}
                        className="h-8 rounded-md border border-line bg-card px-2 text-[12.5px] outline-none focus:border-brand-ring"
                      >
                        <option value="pending">Pending</option>
                        <option value="submitted">Submitted</option>
                        <option value="registered">Registered</option>
                        <option value="rejected">Rejected</option>
                        <option value="na">Doesn't apply</option>
                      </select>
                    </div>
                    {st === "registered" ? (
                      <DatePicker className="h-9" clearable={false} value={r.date || ""} onChange={(d) => setReg(s.key, { date: d })} ariaLabel={`${s.label} registration date`} />
                    ) : st === "rejected" ? (
                      <input className={cn(inputCls, "h-9 text-[12.5px]")} value={r.note || ""} onChange={(e) => setReg(s.key, { note: e.target.value })} placeholder={`Why ${s.label} rejected it…`} aria-label={`Why ${s.label} rejected it`} />
                    ) : (
                      <div className="text-[12px] text-muted2 h-9 flex items-center">{st === "submitted" ? "Sent, waiting to hear back" : st === "na" ? "This song doesn't go here" : "Not registered"}</div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="mt-4 pt-4 border-t border-line grid md:grid-cols-[220px_1fr] gap-4">
              <Field label="Action needed from the writer" hint="The whole song. Writers see it first, with your note, until you turn it off.">
                <label className="flex items-center gap-2.5 h-10 cursor-pointer">
                  <Switch
                    checked={form.reg_status === "Action needed"}
                    onCheckedChange={(v) => setForm((f) => ({ ...f, reg_status: v ? "Action needed" : "", reg_status_note: v ? f.reg_status_note : "" }))}
                    aria-label="Action needed from the writer"
                  />
                  <span className="text-[13px]">{form.reg_status === "Action needed" ? "On" : "Off"}</span>
                </label>
              </Field>
              {form.reg_status === "Action needed" && (
                <Field label="What you need from them">
                  <input className={inputCls} value={form.reg_status_note || ""} onChange={set("reg_status_note")} placeholder="e.g. Please send the correct ISRC" />
                </Field>
              )}
            </div>
          </Card>

          <Card k="dist" hint="Where it went out">
            <div className="grid md:grid-cols-3 gap-4">
              <Field label="Distributor">
                <ListInput id="dists" value={form.distributor} onChange={set("distributor")} options={values.distributor.map((v) => v.value)} />
              </Field>
              <Field label="PRO (as on sheet)">
                <input className={inputCls} value={form.pro} onChange={set("pro")} placeholder={settings.publisher_pro} />
              </Field>
              <Field label="IPI # (as on sheet)">
                <input className={cn(inputCls, "font-mono")} value={form.ipi} onChange={set("ipi")} placeholder={settings.publisher_ipi} />
              </Field>
            </div>
            <Field label="Notes">
              <textarea className={cn(inputCls, "h-24 py-2")} value={form.notes} onChange={set("notes")} />
            </Field>
          </Card>

          {!isNew && (
            <div className="flex gap-2 justify-between pt-2">
              <Button type="button" variant="outline" onClick={remove} className="text-stock-out">
                <Trash2 className="w-4 h-4 mr-2" /> Delete track
              </Button>
              <Button type="button" variant="outline" onClick={duplicate}>
                <Copy className="w-4 h-4 mr-2" /> Duplicate
              </Button>
            </div>
          )}
        </div>

        <aside className="w-full xl:w-[320px] shrink-0 space-y-4 xl:sticky xl:top-[92px]">
          <section className="panel panel-pad">
            <div className="eyebrow">Ready to register</div>
            <div className="flex items-baseline gap-2 mt-2">
              <span className="font-display text-[34px] font-semibold">{passed}</span>
              <span className="text-[13px] text-muted2">of {checks.length} checks pass</span>
            </div>
            <div className="h-2 rounded-full bg-surface mt-3 mb-4">
              <div className="h-2 rounded-full bg-brand transition-all" style={{ width: `${(passed / checks.length) * 100}%` }} />
            </div>
            <ul className="space-y-2.5 text-[13px]">
              {checks.map(([t, ok]) => (
                <li key={t} className="flex items-center gap-2.5">
                  {ok ? <Check className="w-4 h-4 text-stock-in shrink-0" /> : <AlertTriangle className="w-4 h-4 text-gold shrink-0" />}
                  <span className={ok ? "text-ink" : "text-muted2"}>{t}</span>
                </li>
              ))}
            </ul>
          </section>
          {track && (
            <section className="panel panel-pad">
              <div className="eyebrow">History</div>
              <ol className="mt-3 space-y-3.5 text-[12.5px]">
                {[...(track.history || [])].reverse().slice(0, 12).map((h, i) => (
                  <li key={i}>
                    <div className="font-medium text-ink">{h.text}</div>
                    <div className="text-muted2 mt-0.5">
                      {h.by} · {formatDateTime(h.at)}
                    </div>
                  </li>
                ))}
              </ol>
            </section>
          )}
        </aside>
      </form>
    </Layout>
  );
}

/** Shown above a track opened from an album page: where you are, previous / next, and every track to jump to. */
function AlbumBar({ album, pos, dirty, busy, pending, onGo, onBack, onSaveGo, onDiscardGo, onStay }) {
  const list = album.track_list;
  const last = pos === list.length - 1;
  return (
    <div className="panel mb-4 overflow-hidden" data-testid="album-bar">
      <div className="px-4 py-2.5 flex items-center gap-2.5 flex-wrap">
        <button type="button" onClick={onBack} className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-brand">
          <ArrowLeft className="w-3.5 h-3.5" /> {album.name || "Album"}
        </button>
        <span className="text-[12.5px] text-muted2">
          Track {pos + 1} of {list.length}
        </span>
        <span className="flex-1" />
        <span className="hidden md:inline text-[11.5px] text-muted2 mr-1">
          <kbd className="font-mono">[</kbd> and <kbd className="font-mono">]</kbd> to step
        </span>
        <Button size="sm" variant="outline" onClick={() => onGo(pos - 1)} disabled={pos === 0 || busy} aria-label="Previous track">
          <ChevronLeft className="w-4 h-4 sm:mr-1" />
          <span className="hidden sm:inline">Previous</span>
        </Button>
        {dirty && !last ? (
          <Button size="sm" className="bg-brand hover:bg-brand-hover" onClick={() => onSaveGo(pos + 1)} disabled={busy}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save & next"}
            <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        ) : (
          <Button size="sm" variant={dirty ? "outline" : "default"} className={dirty ? "" : "bg-brand hover:bg-brand-hover"} onClick={() => onGo(pos + 1)} disabled={last || busy} aria-label="Next track">
            <span className="hidden sm:inline">Next</span>
            <ChevronRight className="w-4 h-4 sm:ml-1" />
          </Button>
        )}
      </div>
      <div className="px-4 pb-2.5 flex gap-1.5 overflow-x-auto" role="tablist" aria-label="Tracks on this album">
        {list.map((t, j) => (
          <button
            key={t.track_id}
            type="button"
            role="tab"
            aria-selected={j === pos}
            ref={j === pos ? (el) => el && (el.parentElement.scrollLeft = el.offsetLeft - el.parentElement.clientWidth / 2 + el.offsetWidth / 2) : undefined}
            onClick={() => onGo(j)}
            className={cn(
              "shrink-0 inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full border text-[12px] whitespace-nowrap",
              j === pos ? "border-brand bg-brand-light text-ink font-semibold" : "border-line text-muted2 hover:text-ink hover:bg-surface"
            )}
          >
            <b className="font-mono text-[11px]">{j + 1}</b>
            {t.title}
            {j === pos && dirty && <span className="w-1.5 h-1.5 rounded-full bg-gold" title="Unsaved changes" />}
          </button>
        ))}
      </div>
      {pending != null && (
        <div className="px-4 py-2.5 border-t border-line bg-gold-light flex items-center gap-2 flex-wrap" role="alert">
          <span className="text-[13px] flex-1 min-w-[180px]">
            You changed <b>{list[pos].title}</b>. Save before going to <b>{list[pending]?.title}</b>?
          </span>
          <Button size="sm" className="bg-brand hover:bg-brand-hover" onClick={() => onSaveGo(pending)} disabled={busy}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save and go"}
          </Button>
          <Button size="sm" variant="outline" onClick={() => onDiscardGo(pending)} disabled={busy}>
            Go without saving
          </Button>
          <Button size="sm" variant="ghost" onClick={onStay} disabled={busy}>
            Stay
          </Button>
        </div>
      )}
    </div>
  );
}
